import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { RentalAgreement, RentalUsageDay, RentalUsageDayAudit, RentalUsageException } from '../rental.model';
import { RentalService } from '../rental.service';

interface UsageCalendarCell {
    date: string;
    usages: RentalUsageDay[];
}

interface UsageCalendarRow {
    resource: string;
    agreement: string;
    cells: UsageCalendarCell[];
}

@Component({
    selector: 'app-rental-usage',
    standalone: true,
    templateUrl: './rental-usage.component.html',
    styleUrls: ['./rental-usage.component.css'],
    imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, DialogModule, IftaLabelModule, InputGroupAddonModule, InputGroupModule, ProgressBarModule, SelectModule, TableModule, TabsModule, TagModule, TextareaModule, ToastModule, TooltipModule, PageShellComponent, PageHeroComponent, PageDataPanelComponent],
    providers: [MessageService]
})
export class RentalUsageComponent implements OnInit {
    fromDate = this.addDays(this.today(), -29);
    toDate = new Date();
    agreementId?: number | null;
    agreements: RentalAgreement[] = [];
    usageDays: RentalUsageDay[] = [];
    exceptions: RentalUsageException[] = [];
    approvalQueue: RentalUsageDay[] = [];
    audit: RentalUsageDayAudit[] = [];
    selectedUsage?: RentalUsageDay;
    pendingAction = '';
    actionNotes = '';
    actionDialogVisible = false;
    auditDialogVisible = false;
    activeTab: string | number = 'exceptions';
    calendarDays: Date[] = [];
    calendarRows: UsageCalendarRow[] = [];
    loading = false;
    saving = false;
    readonly canManage: boolean;
    readonly canSubmit: boolean;
    readonly canApprove: boolean;
    private readonly agreementReferences = new Map<number, string>();
    private readonly agreementResourceReferences = new Map<number, string>();
    private readonly fleetResourceReferences = new Map<string, string>();
    private readonly loadingAgreementDetails = new Set<number>();
    private readonly hydratedAgreementDetails = new Set<number>();

    constructor(private rentalService: RentalService, private session: SessionStorageService, private permissions: PermissionService, private route: ActivatedRoute, private messages: MessageService) {
        this.canManage = permissions.hasPermission(app_permissions.fleet.rental.manage);
        this.canSubmit = permissions.hasPermission(app_permissions.fleet.rental.usage.submit);
        this.canApprove = permissions.hasPermission(app_permissions.fleet.rental.usage.approve);
    }

    ngOnInit(): void {
        const routeAgreementId = Number(this.route.snapshot.queryParamMap.get('agreementId'));
        this.agreementId = Number.isInteger(routeAgreementId) && routeAgreementId > 0 ? routeAgreementId : null;
        this.loadAgreementOptions();
        this.load();
    }

    load(): void {
        const companyId = this.companyId();
        if (!companyId) return;
        this.loading = true;
        this.rentalService.searchUsageDays(companyId, this.agreementId || undefined, undefined, this.isoDate(this.fromDate), this.isoDate(this.toDate)).subscribe({ next: response => { this.usageDays = response.items ?? []; this.hydrateReferences(this.usageDays); this.rebuildCalendar(); this.loading = false; }, error: error => { this.loading = false; this.error(error?.error?.message || 'Could not load rental usage.'); } });
        this.rentalService.searchUsageExceptions(companyId).subscribe({ next: response => { this.exceptions = response.items ?? []; this.hydrateReferences(this.exceptions); }, error: () => this.exceptions = [] });
        if (this.canApprove) this.rentalService.searchUsageDays(companyId, this.agreementId || undefined, 'SUBMITTED', this.isoDate(this.fromDate), this.isoDate(this.toDate)).subscribe({ next: response => { this.approvalQueue = response.items ?? []; this.hydrateReferences(this.approvalQueue); }, error: () => this.approvalQueue = [] });
    }

    reconcile(): void {
        const companyId = this.companyId();
        if (!companyId || !this.fromDate || !this.toDate) return;
        this.saving = true;
        this.rentalService.reconcileUsageDays(companyId, this.isoDate(this.fromDate)!, this.isoDate(this.toDate)!).subscribe({ next: response => { this.saving = false; const result = response.data; this.success(`${result?.createdUsageDays || 0} usage day(s) created and ${result?.linkedExistingUsageDays || 0} linked.`); this.load(); }, error: error => { this.saving = false; this.error(error?.error?.message || 'Rental reconciliation could not be completed.'); } });
    }

    shiftCalendarRange(days: number): void {
        this.fromDate = this.addDays(this.fromDate, days);
        this.toDate = this.addDays(this.toDate, days);
        this.load();
    }

    resetCalendarRange(): void {
        this.toDate = this.today();
        this.fromDate = this.addDays(this.toDate, -29);
        this.load();
    }

    resolve(exception: RentalUsageException): void {
        if (!exception.rentalUsageExceptionId) return;
        this.saving = true;
        this.rentalService.resolveUsageException(exception.rentalUsageExceptionId).subscribe({ next: () => { this.saving = false; this.success('Usage exception resolved.'); this.load(); }, error: error => { this.saving = false; this.error(error?.error?.message || 'Could not resolve the usage exception.'); } });
    }

    openAction(row: RentalUsageDay, action: string): void { this.selectedUsage = row; this.pendingAction = action; this.actionNotes = ''; this.actionDialogVisible = true; }
    submitAction(): void {
        if (!this.selectedUsage?.rentalUsageDayId || !this.pendingAction) return;
        this.saving = true;
        this.rentalService.changeUsageStatus(this.selectedUsage.rentalUsageDayId, this.pendingAction, this.actionNotes).subscribe({ next: () => { this.saving = false; this.actionDialogVisible = false; this.success(`Usage day ${this.actionLabel(this.pendingAction)}.`); this.load(); this.openAudit(this.selectedUsage!); }, error: error => { this.saving = false; this.error(error?.error?.message || `Could not ${this.pendingAction} the usage day.`); } });
    }

    bulkApprove(): void {
        const companyId = this.companyId();
        const ids = this.approvalQueue.map(row => row.rentalUsageDayId).filter((id): id is number => id != null);
        if (!companyId || !ids.length) return;
        this.saving = true;
        this.rentalService.bulkApproveUsage(companyId, ids).subscribe({ next: rows => { this.saving = false; this.success(`${rows.length} usage day(s) approved.`); this.load(); }, error: error => { this.saving = false; this.error(error?.error?.message || 'Could not approve the visible usage queue.'); } });
    }

    openAudit(row: RentalUsageDay): void {
        if (!row.rentalUsageDayId) return;
        this.selectedUsage = row;
        this.audit = [];
        this.auditDialogVisible = true;
        this.rentalService.getUsageAudit(row.rentalUsageDayId).subscribe({ next: result => this.audit = result, error: () => this.audit = [] });
    }

    agreementReference(id?: number | null): string {
        return id ? this.agreementReferences.get(id) || 'Loading agreement reference…' : '-';
    }

    resourceReference(agreementResourceId?: number | null): string {
        return agreementResourceId ? this.agreementResourceReferences.get(agreementResourceId) || 'Loading resource reference…' : '-';
    }

    exceptionResourceReference(row: RentalUsageException): string {
        const key = this.fleetResourceKey(row.resourceType, row.resourceId);
        return key ? this.fleetResourceReferences.get(key) || 'Loading resource reference…' : '-';
    }

    exceptionSourceReference(row: RentalUsageException): string {
        return row.sourceType ? `${this.titleCase(row.sourceType)} record` : '-';
    }

    auditActorReference(actorCustomerId?: number | null): string {
        return actorCustomerId ? 'Portal user' : 'System';
    }

    calendarDayLabel(date: Date): string { return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(date).slice(0, 1); }
    calendarDayNumber(date: Date): string { return String(date.getDate()); }
    calendarCellLabel(cell: UsageCalendarCell): string {
        if (!cell.usages.length) return 'No usage';
        const amount = cell.usages.reduce((total, usage) => total + Number(usage.amountSnapshot || 0), 0);
        const currency = cell.usages[0].currencyCode || '';
        const status = cell.usages.map(usage => usage.usageStatus || 'PROVISIONAL').join(', ');
        return `${cell.usages.length} usage day${cell.usages.length === 1 ? '' : 's'}: ${amount.toFixed(2)} ${currency} (${status})`;
    }
    calendarCellClass(cell: UsageCalendarCell): string {
        if (!cell.usages.length) return 'empty';
        return (cell.usages[0].usageStatus || 'PROVISIONAL').toLowerCase();
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' { return status === 'APPROVED' ? 'success' : status === 'PROVISIONAL' ? 'info' : status === 'SUBMITTED' ? 'warning' : status === 'REJECTED' || status === 'REVERSED' ? 'danger' : 'secondary'; }
    private actionLabel(action: string): string { return action === 'submit' ? 'submitted' : action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'reversed'; }
    private loadAgreementOptions(): void {
        const companyId = this.companyId();
        if (!companyId) return;
        this.rentalService.searchAgreements(companyId).subscribe({
            next: response => {
                this.agreements = response.items ?? [];
                this.agreements.forEach(agreement => {
                    if (agreement.rentalAgreementId) this.agreementReferences.set(agreement.rentalAgreementId, agreement.agreementNumber || `Agreement ${agreement.rentalAgreementId}`);
                });
                this.hydrateReferences(this.usageDays, this.exceptions, this.approvalQueue);
            },
            error: () => this.agreements = []
        });
    }
    private hydrateReferences(...groups: Array<Array<RentalUsageDay | RentalUsageException>>): void {
        const ids = new Set<number>();
        groups.flat().forEach(row => { if (row.rentalAgreementId) ids.add(row.rentalAgreementId); });
        ids.forEach(id => {
            if (this.loadingAgreementDetails.has(id) || this.hydratedAgreementDetails.has(id)) return;
            this.loadingAgreementDetails.add(id);
            this.rentalService.getAgreement(id).subscribe({
                next: response => {
                    const agreement = response.data;
                    if (agreement?.rentalAgreementId) this.agreementReferences.set(agreement.rentalAgreementId, agreement.agreementNumber || `Agreement ${agreement.rentalAgreementId}`);
                    (agreement?.resources ?? []).forEach(resource => {
                        const label = resource.resourceLabel || `${this.titleCase(resource.resourceType || 'resource')} reference`;
                        if (resource.rentalAgreementResourceId) this.agreementResourceReferences.set(resource.rentalAgreementResourceId, label);
                        const key = this.fleetResourceKey(resource.resourceType, resource.resourceId);
                        if (key) this.fleetResourceReferences.set(key, label);
                    });
                    this.rebuildCalendar();
                    this.hydratedAgreementDetails.add(id);
                },
                error: () => this.loadingAgreementDetails.delete(id),
                complete: () => this.loadingAgreementDetails.delete(id)
            });
        });
    }
    private fleetResourceKey(type?: string, id?: number): string | undefined { return type && id ? `${type}:${id}` : undefined; }
    private rebuildCalendar(): void {
        this.calendarDays = this.buildDateRange(this.fromDate, this.toDate);
        const usageByResource = new Map<string, RentalUsageDay[]>();
        this.usageDays.forEach(usage => {
            const key = `${usage.rentalAgreementId || 'unassigned'}:${usage.rentalAgreementResourceId || 'unassigned'}`;
            const rows = usageByResource.get(key) || [];
            rows.push(usage);
            usageByResource.set(key, rows);
        });
        this.calendarRows = Array.from(usageByResource.values()).map(usages => {
            const first = usages[0];
            return {
                resource: this.resourceReference(first.rentalAgreementResourceId),
                agreement: this.agreementReference(first.rentalAgreementId),
                cells: this.calendarDays.map(date => {
                    const iso = this.isoDate(date);
                    return { date: iso, usages: usages.filter(usage => usage.workDate === iso) };
                })
            };
        }).sort((left, right) => left.resource.localeCompare(right.resource));
    }
    private buildDateRange(from: Date, to: Date): Date[] {
        if (!from || !to || from > to) return [];
        const dates: Date[] = [];
        for (let date = new Date(from.getFullYear(), from.getMonth(), from.getDate()); date <= to; date = this.addDays(date, 1)) dates.push(date);
        return dates;
    }
    private today(): Date { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), now.getDate()); }
    private addDays(date: Date, days: number): Date { const result = new Date(date.getFullYear(), date.getMonth(), date.getDate()); result.setDate(result.getDate() + days); return result; }
    private titleCase(value: string): string { return value.toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' '); }
    private companyId(): number { return this.session.getCompany()?.id ?? 0; }
    private isoDate(value: Date): string { return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`; }
    private success(detail: string): void { this.messages.add({ severity: 'success', summary: 'Rental usage', detail }); }
    private error(detail: string): void { this.messages.add({ severity: 'error', summary: 'Rental usage', detail }); }
}
